(() => {
  'use strict';

  const CONFIG = Object.freeze({
    schemaVersion: 2,
    profileKey: 'sct_rec_profile_v2',
    historyKey: 'sct_rec_history_v2',
    sessionViewedKey: 'sct_rec_session_viewed_v2',
    sessionAtcKey: 'sct_rec_session_atc_v1',
    recommendationAttributionKey: 'sct_rec_last_click_v1',
    experimentOutcomeQueueKey: 'sct_rec_experiment_outcomes_v1',
    coViewKey: 'sct_rec_coview_v1',
    coViewSessionSequenceKey: 'sct_rec_coview_session_v1',
    styleMapKey: 'sct_rec_style_brand_map_v1',
    purchaseQueueKey: 'sct_rec_purchase_queue_v1',
    processedOrdersKey: 'sct_rec_purchase_orders_v1',
    maxHistoryEvents: 100,
    maxViewCreditsPerProduct: 3,
    maxClickCreditsPerProduct: 3,
    maxAddToCartCreditsPerProduct: 3,
    maxCoViewSources: 60,
    maxCoViewTargetsPerSource: 20,
    maxCoViewSessionSequence: 20,
    weights: Object.freeze({
      view: 1,
      click: 1,
      add_to_cart: 3,
      purchase: 10,
    }),
    dimensionWeights: Object.freeze({
      style: 1,
      brand: 1,
      category: 0.5,
      price: 1,
    }),
  });

  const query = new URLSearchParams(window.location.search);
  const debug = query.get('sct_debug') === '1';
  const context = readJsonScript('SctRecommendationContext') || {};
  const currentProduct = normalizeProduct(context.product);

  function readJsonScript(id) {
    const node = document.getElementById(id);
    if (!node) return null;

    try {
      return JSON.parse(node.textContent || '{}');
    } catch (error) {
      if (debug) {
        console.warn('[SCT Recommendation] Failed to parse context.', error);
      }
      return null;
    }
  }

  function safeParse(value, fallback) {
    try {
      const parsed = JSON.parse(value);
      return parsed ?? fallback;
    } catch (error) {
      return fallback;
    }
  }

  function readLocal(key, fallback) {
    try {
      return safeParse(localStorage.getItem(key), fallback);
    } catch (error) {
      return fallback;
    }
  }

  function writeLocal(key, value) {
    try {
      localStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch (error) {
      if (debug) {
        console.warn('[SCT Recommendation] localStorage write failed.', error);
      }
      return false;
    }
  }

  function readSession(key, fallback) {
    try {
      return safeParse(sessionStorage.getItem(key), fallback);
    } catch (error) {
      return fallback;
    }
  }

  function writeSession(key, value) {
    try {
      sessionStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch (error) {
      return false;
    }
  }

  function markSessionAddToCart() {
    return writeSession(
      CONFIG.sessionAtcKey,
      true
    );
  }

  function storeRecommendationAttribution(
    rawProduct,
    meta = {}
  ) {
    const product =
      normalizeProduct(
        rawProduct
      );

    const source =
      cleanText(
        meta
          ?.recommendation_source
      );

    if (
      !product ||
      !source
    ) {
      return false;
    }

    return writeSession(
      CONFIG.recommendationAttributionKey,
      {
        handle:
          product.handle,

        source,

        clickedAt:
          Date.now(),
      }
    );
  }

  function recommendationAttributionFor(
    handle
  ) {
    const attribution =
      readSession(
        CONFIG.recommendationAttributionKey,
        null
      );

    if (
      !attribution ||
      typeof attribution !==
        'object'
    ) {
      return null;
    }

    if (
      cleanText(
        attribution.handle
      ) !== cleanText(handle)
    ) {
      return null;
    }

    const clickedAt =
      Number(
        attribution.clickedAt
      ) || 0;

    if (
      !clickedAt ||
      Date.now() - clickedAt >
        2 * 60 * 60 * 1000
    ) {
      return null;
    }

    return attribution;
  }

  function queueExperimentOutcome(
    actionType,
    rawProduct,
    extra = {}
  ) {
    const product =
      normalizeProduct(
        rawProduct
      );

    const attribution =
      product
        ? recommendationAttributionFor(
            product.handle
          )
        : null;

    const payload = {
      product_id:
        product?.id,

      product_handle:
        product?.handle,

      product_title:
        product?.title,

      brand:
        product?.vendor,

      category:
        product?.category,

      price_cents:
        product?.price_cents,

      recommendation_attributed:
        attribution
          ? true
          : false,

      recommendation_source:
        attribution?.source,

      ...extra,
    };

    if (
      window.SctAnalytics
        ?.trackExperiment
    ) {
      window.SctAnalytics
        .trackExperiment(
          actionType,
          payload
        );

      return;
    }

    const queue =
      readSession(
        CONFIG.experimentOutcomeQueueKey,
        []
      );

    const safeQueue =
      Array.isArray(queue)
        ? queue
        : [];

    safeQueue.push({
      action_type:
        actionType,

      payload,
    });

    writeSession(
      CONFIG.experimentOutcomeQueueKey,
      safeQueue.slice(-20)
    );
  }

  function globalCoViewEndpoint() {
    return cleanText(
      context.global_coview_api
    ).replace(/\/$/, '');
  }

  function isPreviewOrEditor() {
    return Boolean(
      context.design_mode ||
      window.Shopify?.designMode ||
      query.has(
        'preview_theme_id'
      )
    );
  }

  function analyticsConsentAllowed() {
    const privacy =
      window.Shopify
        ?.customerPrivacy;

    if (
      !privacy ||
      typeof privacy
        .analyticsProcessingAllowed !==
        'function'
    ) {
      return true;
    }

    try {
      return Boolean(
        privacy
          .analyticsProcessingAllowed()
      );
    } catch (_error) {
      return false;
    }
  }

  function sendGlobalCoViewTransition(
    fromHandle,
    toHandle
  ) {
    const from =
      cleanText(fromHandle);

    const to =
      cleanText(toHandle);

    const api =
      globalCoViewEndpoint();

    if (
      !api ||
      !from ||
      !to ||
      from === to ||
      isPreviewOrEditor() ||
      !analyticsConsentAllowed()
    ) {
      return;
    }

    const market =
      cleanText(
        context.market
      ) ||
      cleanText(
        context.country
      ) ||
      'global';

    fetch(
      api + '/event',
      {
        method:
          'POST',

        mode:
          'cors',

        keepalive:
          true,

        headers: {
          'Content-Type':
            'application/json',
        },

        body:
          JSON.stringify({
            from_product:
              from,

            to_product:
              to,

            market,
          }),
      }
    )
      .then(
        response => {
          if (
            debug &&
            !response.ok
          ) {
            console.warn(
              '[SCT Recommendation] Global Co-view event failed:',
              response.status
            );
          }
        }
      )
      .catch(
        error => {
          if (debug) {
            console.warn(
              '[SCT Recommendation] Global Co-view event error.',
              error
            );
          }
        }
      );
  }

  function emptyCoViewMap() {
    return {
      schemaVersion: 1,
      updatedAt: null,
      edges: {},
    };
  }

  function loadCoViewMap() {
    const stored =
      readLocal(
        CONFIG.coViewKey,
        null
      );

    if (
      !stored ||
      typeof stored !== 'object' ||
      Number(
        stored.schemaVersion
      ) !== 1 ||
      !stored.edges ||
      typeof stored.edges !==
        'object' ||
      Array.isArray(
        stored.edges
      )
    ) {
      return emptyCoViewMap();
    }

    return {
      schemaVersion: 1,
      updatedAt:
        stored.updatedAt ||
        null,
      edges:
        stored.edges,
    };
  }

  function trimCoViewTargets(
    targets
  ) {
    if (
      !targets ||
      typeof targets !== 'object' ||
      Array.isArray(targets)
    ) {
      return {};
    }

    return Object.fromEntries(
      Object
        .entries(targets)
        .filter(
          ([handle, value]) =>
            cleanText(handle) &&
            value &&
            typeof value ===
              'object' &&
            Number(value.score) > 0
        )
        .sort(
          (a, b) => {
            const scoreDiff =
              Number(
                b[1]?.score
              ) -
              Number(
                a[1]?.score
              );

            if (scoreDiff !== 0) {
              return scoreDiff;
            }

            return (
              Number(
                b[1]?.lastAt
              ) -
              Number(
                a[1]?.lastAt
              )
            );
          }
        )
        .slice(
          0,
          CONFIG
            .maxCoViewTargetsPerSource
        )
    );
  }

  function trimCoViewMap(
    map
  ) {
    const sourceEntries =
      Object
        .entries(
          map.edges || {}
        )
        .map(
          ([source, targets]) => {
            const cleanTargets =
              trimCoViewTargets(
                targets
              );

            const sourceScore =
              Object
                .values(
                  cleanTargets
                )
                .reduce(
                  (sum, edge) =>
                    sum +
                    Number(
                      edge?.score ||
                      0
                    ),
                  0
                );

            const lastAt =
              Object
                .values(
                  cleanTargets
                )
                .reduce(
                  (latest, edge) =>
                    Math.max(
                      latest,
                      Number(
                        edge
                          ?.lastAt ||
                        0
                      )
                    ),
                  0
                );

            return {
              source,
              targets:
                cleanTargets,
              sourceScore,
              lastAt,
            };
          }
        )
        .filter(
          item =>
            cleanText(
              item.source
            ) &&
            item.sourceScore > 0
        )
        .sort(
          (a, b) =>
            b.lastAt -
              a.lastAt ||
            b.sourceScore -
              a.sourceScore
        )
        .slice(
          0,
          CONFIG.maxCoViewSources
        );

    map.edges =
      Object.fromEntries(
        sourceEntries.map(
          item => [
            item.source,
            item.targets,
          ]
        )
      );

    map.updatedAt =
      Date.now();

    return map;
  }

  function addCoViewEdge(
    map,
    sourceHandle,
    targetHandle,
    amount
  ) {
    const source =
      cleanText(
        sourceHandle
      );

    const target =
      cleanText(
        targetHandle
      );

    const score =
      Number(amount);

    if (
      !source ||
      !target ||
      source === target ||
      !Number.isFinite(score) ||
      score <= 0
    ) {
      return;
    }

    if (
      !map.edges[source] ||
      typeof map.edges[source] !==
        'object'
    ) {
      map.edges[source] = {};
    }

    const existing =
      map.edges[source][target] ||
      {
        score: 0,
        count: 0,
        lastAt: 0,
      };

    map.edges[source][target] = {
      score:
        Math.min(
          20,
          Number(
            existing.score ||
            0
          ) + score
        ),

      count:
        Math.min(
          99,
          Number(
            existing.count ||
            0
          ) + 1
        ),

      lastAt:
        Date.now(),
    };
  }

  function recordCoViewTransition(
    currentHandle
  ) {
    const current =
      cleanText(
        currentHandle
      );

    if (!current) {
      return;
    }

    const storedSequence =
      readSession(
        CONFIG
          .coViewSessionSequenceKey,
        []
      );

    const sequence =
      Array.isArray(
        storedSequence
      )
        ? storedSequence
            .map(cleanText)
            .filter(Boolean)
        : [];

    const recent =
      sequence
        .filter(
          handle =>
            handle !== current
        )
        .slice(-3);

    const distanceWeights =
      [0.25, 0.5, 1];

    const directPrevious =
      recent.length
        ? recent[
            recent.length - 1
          ]
        : '';

    if (directPrevious) {
      sendGlobalCoViewTransition(
        directPrevious,
        current
      );
    }

    if (recent.length) {
      const map =
        loadCoViewMap();

      recent.forEach(
        (source, index) => {
          const weightIndex =
            distanceWeights.length -
            recent.length +
            index;

          addCoViewEdge(
            map,
            source,
            current,
            distanceWeights[
              weightIndex
            ] ||
            0.25
          );
        }
      );

      writeLocal(
        CONFIG.coViewKey,
        trimCoViewMap(map)
      );
    }

    sequence.push(
      current
    );

    writeSession(
      CONFIG
        .coViewSessionSequenceKey,
      sequence.slice(
        -CONFIG
          .maxCoViewSessionSequence
      )
    );
  }

  function coViewEdge(
    sourceHandle,
    targetHandle
  ) {
    const source =
      cleanText(
        sourceHandle
      );

    const target =
      cleanText(
        targetHandle
      );

    if (
      !source ||
      !target ||
      source === target
    ) {
      return null;
    }

    const map =
      loadCoViewMap();

    const edge =
      map
        ?.edges
        ?.[source]
        ?.[target];

    if (
      !edge ||
      typeof edge !==
        'object'
    ) {
      return null;
    }

    return {
      score:
        Number(
          edge.score
        ) || 0,

      count:
        Number(
          edge.count
        ) || 0,

      lastAt:
        Number(
          edge.lastAt
        ) || 0,
    };
  }

  function persistStyleBrandMap() {
    const map = context.style_brand_map;

    if (
      !map ||
      typeof map !== 'object' ||
      Array.isArray(map)
    ) {
      return false;
    }

    return writeLocal(
      CONFIG.styleMapKey,
      map
    );
  }

  function stylesForVendorFromContext(vendor) {
    const cleanVendor = cleanText(vendor);
    const map = context.style_brand_map;

    if (!cleanVendor || !map || typeof map !== 'object' || Array.isArray(map)) {
      return [];
    }

    return Object.entries(map)
      .filter(([, vendors]) => Array.isArray(vendors) && vendors.includes(cleanVendor))
      .map(([style]) => style);
  }

  function findStoredProductByIds(profile, productId, variantId) {
    const productIdText = productId ? String(productId) : '';
    const variantIdText = variantId ? String(variantId) : '';

    return Object.values(profile.products || {}).find(item => {
      if (productIdText && String(item?.id || '') === productIdText) {
        return true;
      }

      if (
        variantIdText &&
        Array.isArray(item?.variant_ids) &&
        item.variant_ids.map(String).includes(variantIdText)
      ) {
        return true;
      }

      return false;
    }) || null;
  }

  function nowIso() {
    return new Date().toISOString();
  }

  function cleanText(value) {
    if (typeof value !== 'string') return '';
    return value.trim();
  }

  function normalizeProduct(product) {
    if (!product || typeof product !== 'object') return null;

    const handle = cleanText(product.handle);
    if (!handle) return null;

    const styles = Array.isArray(product.styles)
      ? [...new Set(product.styles.map(cleanText).filter(Boolean))]
      : [];

    const priceCents = Number(product.price_cents);

    return {
      id: product.id ?? null,
      variant_ids: Array.isArray(product.variant_ids)
        ? [...new Set(product.variant_ids.map(value => String(value)).filter(Boolean))]
        : [],
      handle,
      title: cleanText(product.title),
      vendor: cleanText(product.vendor),
      category: cleanText(product.category),
      price_cents: Number.isFinite(priceCents) ? Math.max(0, priceCents) : 0,
      available: product.available !== false,
      created_at: product.created_at || null,
      styles,
    };
  }

  function emptyProfile() {
    return {
      schemaVersion: CONFIG.schemaVersion,
      updatedAt: null,
      totalCreditedEvents: 0,
      uniqueViewedProducts: 0,
      styles: {},
      brands: {},
      categories: {},
      price: {
        weightedSumCents: 0,
        weightTotal: 0,
        averageCents: 0,
      },
      products: {},
    };
  }

  function sanitizeScoreMap(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return {};

    return Object.fromEntries(
      Object.entries(value)
        .filter(([key, score]) => cleanText(key) && Number.isFinite(Number(score)))
        .map(([key, score]) => [cleanText(key), Number(score)])
    );
  }

  function loadProfile() {
    const stored = readLocal(CONFIG.profileKey, null);

    if (
      !stored ||
      typeof stored !== 'object' ||
      Number(stored.schemaVersion) !== CONFIG.schemaVersion
    ) {
      return emptyProfile();
    }

    const profile = emptyProfile();

    profile.updatedAt = stored.updatedAt || null;
    profile.totalCreditedEvents = Number(stored.totalCreditedEvents) || 0;
    profile.styles = sanitizeScoreMap(stored.styles);
    profile.brands = sanitizeScoreMap(stored.brands);
    profile.categories = sanitizeScoreMap(stored.categories);

    const storedPrice = stored.price || {};
    profile.price.weightedSumCents = Number(storedPrice.weightedSumCents) || 0;
    profile.price.weightTotal = Number(storedPrice.weightTotal) || 0;
    profile.price.averageCents = Number(storedPrice.averageCents) || 0;

    if (stored.products && typeof stored.products === 'object' && !Array.isArray(stored.products)) {
      profile.products = stored.products;
    }

    profile.uniqueViewedProducts = Object.values(profile.products)
      .filter(item => Number(item?.viewCredits) > 0)
      .length;

    return profile;
  }

  function incrementScore(map, key, amount) {
    const cleanKey = cleanText(key);
    if (!cleanKey || !Number.isFinite(amount) || amount <= 0) return;

    map[cleanKey] = (Number(map[cleanKey]) || 0) + amount;
  }

  function productState(profile, product) {
    const existing = profile.products[product.handle];

    if (existing && typeof existing === 'object') {
      return existing;
    }

    const created = {
      id: product.id,
      variant_ids: product.variant_ids,
      handle: product.handle,
      vendor: product.vendor,
      category: product.category,
      styles: product.styles,
      price_cents: product.price_cents,
      viewCredits: 0,
      clickCredits: 0,
      addToCartCredits: 0,
      purchaseCredits: 0,
      lastEventAt: null,
    };

    profile.products[product.handle] = created;
    return created;
  }

  function eventCreditField(eventType) {
    if (eventType === 'view') return 'viewCredits';
    if (eventType === 'click') return 'clickCredits';
    if (eventType === 'add_to_cart') return 'addToCartCredits';
    if (eventType === 'purchase') return 'purchaseCredits';
    return null;
  }

  function appendHistory(eventType, product, weight) {
    const history = readLocal(CONFIG.historyKey, []);
    const safeHistory = Array.isArray(history) ? history : [];

    safeHistory.unshift({
      eventType,
      weight,
      handle: product.handle,
      vendor: product.vendor,
      category: product.category,
      styles: product.styles,
      price_cents: product.price_cents,
      at: nowIso(),
    });

    writeLocal(
      CONFIG.historyKey,
      safeHistory.slice(0, CONFIG.maxHistoryEvents)
    );
  }

  function applyEvent(eventType, rawProduct) {
    const product = normalizeProduct(rawProduct);
    const weight = Number(CONFIG.weights[eventType]);

    if (!product || !Number.isFinite(weight) || weight <= 0) {
      return { credited: false, reason: 'invalid_event' };
    }

    const profile = loadProfile();
    const state = productState(profile, product);
    const creditField = eventCreditField(eventType);

    if (!creditField) {
      return { credited: false, reason: 'unsupported_event' };
    }

    if (
      eventType === 'view' &&
      Number(state.viewCredits) >= CONFIG.maxViewCreditsPerProduct
    ) {
      return { credited: false, reason: 'view_cap_reached', profile };
    }

    if (
      eventType === 'click' &&
      Number(state.clickCredits) >= CONFIG.maxClickCreditsPerProduct
    ) {
      return { credited: false, reason: 'click_cap_reached', profile };
    }

    if (
      eventType === 'add_to_cart' &&
      Number(state.addToCartCredits) >= CONFIG.maxAddToCartCreditsPerProduct
    ) {
      return { credited: false, reason: 'add_to_cart_cap_reached', profile };
    }

    state[creditField] = (Number(state[creditField]) || 0) + 1;
    state.lastEventAt = nowIso();
    state.variant_ids = product.variant_ids;
    state.vendor = product.vendor;
    state.category = product.category;
    state.styles = product.styles;
    state.price_cents = product.price_cents;

    product.styles.forEach(style => {
      incrementScore(
        profile.styles,
        style,
        weight * CONFIG.dimensionWeights.style
      );
    });

    incrementScore(
      profile.brands,
      product.vendor,
      weight * CONFIG.dimensionWeights.brand
    );

    incrementScore(
      profile.categories,
      product.category,
      weight * CONFIG.dimensionWeights.category
    );

    if (product.price_cents > 0) {
      const priceWeight =
        weight * CONFIG.dimensionWeights.price;

      profile.price.weightedSumCents +=
        product.price_cents * priceWeight;

      profile.price.weightTotal +=
        priceWeight;
      profile.price.averageCents = Math.round(
        profile.price.weightedSumCents / profile.price.weightTotal
      );
    }

    profile.totalCreditedEvents += 1;
    profile.uniqueViewedProducts = Object.values(profile.products)
      .filter(item => Number(item?.viewCredits) > 0)
      .length;
    profile.updatedAt = nowIso();

    const saved = writeLocal(CONFIG.profileKey, profile);

    if (saved) {
      appendHistory(eventType, product, weight);

      document.dispatchEvent(
        new CustomEvent('sct:recommendation_profile_updated', {
          detail: {
            eventType,
            productHandle: product.handle,
            profile,
          },
        })
      );
    }

    return { credited: saved, reason: saved ? 'credited' : 'storage_failed', profile };
  }

  function creditCurrentViewOncePerSession() {
    if (!currentProduct) return;

    const viewed = readSession(CONFIG.sessionViewedKey, []);
    const handles = Array.isArray(viewed) ? viewed.filter(Boolean) : [];

    if (handles.includes(currentProduct.handle)) {
      if (debug) {
        console.log(
          '[SCT Recommendation] View not credited again in this session:',
          currentProduct.handle
        );
      }
      return;
    }

    const result = applyEvent('view', currentProduct);

    if (result.credited || result.reason === 'view_cap_reached') {
      recordCoViewTransition(
        currentProduct.handle
      );

      handles.push(
        currentProduct.handle
      );

      writeSession(
        CONFIG.sessionViewedKey,
        [...new Set(handles)]
      );
    }
  }

  function subscribeToSessionCartActivity(attempt = 0) {
    const pubSubReady =
      typeof subscribe === 'function' &&
      typeof PUB_SUB_EVENTS !== 'undefined' &&
      PUB_SUB_EVENTS?.cartUpdate;

    if (!pubSubReady) {
      if (attempt < 20) {
        window.setTimeout(
          () => subscribeToSessionCartActivity(attempt + 1),
          100
        );
      }

      return;
    }

    subscribe(
      PUB_SUB_EVENTS.cartUpdate,
      event => {
        if (
          event?.source !== 'product-form'
        ) {
          return;
        }

        markSessionAddToCart();

        if (debug) {
          console.log(
            '[SCT Recommendation] Session Add to Cart detected.'
          );
        }
      }
    );
  }

  function currentProductMatchesVariant(variantId) {
    if (!currentProduct || variantId === undefined || variantId === null) {
      return false;
    }

    const variants =
      Array.isArray(currentProduct.variant_ids)
        ? currentProduct.variant_ids
        : [];

    return variants.includes(
      String(variantId)
    );
  }

  function subscribeToSuccessfulAddToCart(attempt = 0) {
    if (!currentProduct) {
      return;
    }

    const pubSubReady =
      typeof subscribe === 'function' &&
      typeof PUB_SUB_EVENTS !== 'undefined' &&
      PUB_SUB_EVENTS?.cartUpdate;

    if (!pubSubReady) {
      if (attempt < 20) {
        window.setTimeout(
          () => subscribeToSuccessfulAddToCart(attempt + 1),
          100
        );
      }

      return;
    }

    subscribe(
      PUB_SUB_EVENTS.cartUpdate,
      event => {
        if (
          event?.source !== 'product-form' ||
          !currentProductMatchesVariant(event.productVariantId)
        ) {
          return;
        }

        const result =
          applyEvent(
            'add_to_cart',
            currentProduct
          );

        queueExperimentOutcome(
          'add_to_cart',
          currentProduct,
          {
            preference_credited:
              result.credited,
          }
        );

        if (debug) {
          console.log(
            '[SCT Recommendation] Add to Cart signal:',
            result.reason,
            currentProduct.handle
          );

          debugProfile(
            result.profile ||
            loadProfile()
          );
        }
      }
    );
  }


  function processPurchaseQueue() {
    const queue = readLocal(CONFIG.purchaseQueueKey, []);
    if (!Array.isArray(queue) || queue.length === 0) return;

    const processed = readLocal(CONFIG.processedOrdersKey, []);
    const processedOrders = Array.isArray(processed) ? processed.map(String) : [];
    const processedSet = new Set(processedOrders);
    const newlyProcessed = [];

    queue.forEach(order => {
      if (!order || typeof order !== 'object') return;

      const orderKey = String(order.order_key || '');
      if (orderKey && processedSet.has(orderKey)) return;

      const products = Array.isArray(order.products) ? order.products : [];
      const productKeys = new Set();

      products.forEach(item => {
        if (!item || typeof item !== 'object') return;

        const productId = item.product_id ? String(item.product_id).split('/').pop() : '';
        const variantId = item.variant_id ? String(item.variant_id).split('/').pop() : '';
        const dedupeKey = productId || variantId;

        if (!dedupeKey || productKeys.has(dedupeKey)) return;
        productKeys.add(dedupeKey);

        const profile = loadProfile();
        const stored = findStoredProductByIds(profile, productId, variantId);

        const vendor = cleanText(stored?.vendor || item.vendor);
        const category = cleanText(stored?.category || item.category);
        const title = cleanText(stored?.title || item.title);
        const handle =
          cleanText(stored?.handle) ||
          `purchase-product-${productId || variantId}`;

        const styles =
          Array.isArray(stored?.styles) && stored.styles.length
            ? stored.styles
            : stylesForVendorFromContext(vendor);

        const purchaseProduct = {
          id: productId || stored?.id || null,
          variant_ids: variantId
            ? [variantId]
            : (Array.isArray(stored?.variant_ids) ? stored.variant_ids : []),
          handle,
          title,
          vendor,
          category,
          price_cents: Number(stored?.price_cents || item.price_cents || 0),
          available: true,
          created_at: null,
          styles,
        };

        const purchaseResult =
          applyEvent(
            'purchase',
            purchaseProduct
          );

        queueExperimentOutcome(
          'purchase',
          purchaseProduct,
          {
            order_key:
              orderKey,

            preference_credited:
              purchaseResult.credited,
          }
        );
      });

      if (orderKey) {
        newlyProcessed.push(orderKey);
        processedSet.add(orderKey);
      }
    });

    writeLocal(CONFIG.purchaseQueueKey, []);
    writeLocal(
      CONFIG.processedOrdersKey,
      [...newlyProcessed, ...processedOrders]
        .filter(Boolean)
        .filter((value, index, array) => array.indexOf(value) === index)
        .slice(0, 50)
    );

    if (debug && newlyProcessed.length) {
      console.log('[SCT Recommendation] Purchase queue processed:', newlyProcessed);
    }
  }

  function sortedScores(map) {
    return Object.entries(map || {})
      .sort((a, b) => Number(b[1]) - Number(a[1]));
  }

  function debugProfile(profile) {
    if (!debug) return;

    console.groupCollapsed('[SCT Recommendation] Preference Profile');
    console.log('Context:', context);
    console.log('Profile:', profile);
    console.log('Unique viewed products:', profile.uniqueViewedProducts);
    console.log('Average preferred price:', profile.price.averageCents);
    console.table(sortedScores(profile.styles).map(([name, score]) => ({ name, score })));
    console.table(sortedScores(profile.brands).slice(0, 10).map(([name, score]) => ({ name, score })));
    console.table(sortedScores(profile.categories).map(([name, score]) => ({ name, score })));
    console.groupEnd();
  }

  function resetForDebug() {
    if (!debug) return false;

    try {
      localStorage.removeItem(CONFIG.profileKey);
      localStorage.removeItem(CONFIG.historyKey);
      localStorage.removeItem(CONFIG.coViewKey);
      sessionStorage.removeItem(CONFIG.sessionViewedKey);
      sessionStorage.removeItem(CONFIG.coViewSessionSequenceKey);
      console.log('[SCT Recommendation] Debug profile reset.');
      return true;
    } catch (error) {
      return false;
    }
  }

  window.SctRecommendation = Object.freeze({
    version: CONFIG.schemaVersion,

    getProfile() {
      return loadProfile();
    },

    getHistory() {
      const history = readLocal(CONFIG.historyKey, []);
      return Array.isArray(history) ? history : [];
    },

    getCoViewMap() {
      return loadCoViewMap();
    },

    getCoViewEdge(
      sourceHandle,
      targetHandle
    ) {
      return coViewEdge(
        sourceHandle,
        targetHandle
      );
    },

    track(
      eventType,
      productData = currentProduct,
      meta = {}
    ) {
      if (
        eventType === 'click'
      ) {
        const source =
          cleanText(
            meta
              ?.recommendation_source
          );

        if (source) {
          storeRecommendationAttribution(
            productData,
            meta
          );

          queueExperimentOutcome(
            'recommendation_click',
            productData,
            {
              recommendation_source:
                source,
            }
          );
        }
      }

      const result =
        applyEvent(
          eventType,
          productData
        );

      debugProfile(
        result.profile ||
        loadProfile()
      );

      return result;
    },

    trackCurrent(eventType) {
      const result = applyEvent(eventType, currentProduct);
      debugProfile(result.profile || loadProfile());
      return result;
    },

    reset() {
      return resetForDebug();
    },
  });

  if (debug && query.get('sct_rec_reset') === '1') {
    resetForDebug();
  }

  persistStyleBrandMap();
  processPurchaseQueue();
  creditCurrentViewOncePerSession();
  subscribeToSessionCartActivity();
  subscribeToSuccessfulAddToCart();
  debugProfile(loadProfile());
})();